import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import {
  UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED,
  UTILITY_TYPE_ID_INTERNET,
  UTILITY_PROVIDER_PURCHASE_FLOW_TYPE_HYBRID,
} from 'common/utility/constants';

import { SCREEN_ID_UTILITY_SETUP_PROVIDER_PLAN_CONFIRMATION } from 'client/constants/screenIds';

import { ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_RETURN_EQUIPMENT } from 'client/analytics/eventList';

import {
  SR_API_ACTION_UTILITY_ACTIVATION_TRACK_INTERNET_FLOW,
  SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM_PLAN,
} from 'api/utilityActionTypes';

import { numberWithCommas } from 'common/formatters';
import { hasRoleAnonymous } from 'common/utility/userRoles';
import { canAccessActivation } from 'common/utility/helpers';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withLocationQuery from 'client/helpers/withLocationQuery';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import branch from 'client/core/branch';
import UtilityPartnerActions from 'client/actions/UtilityPartner';
import UtilityActivationActions from 'client/actions/UtilityActivation';

import { BUTTON_THEME_GREEN_ADOBE } from 'client/components/ButtonDeprecated';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrUtilitySetupPage from 'client/components/SrUtilitySetup/SrUtilitySetupPage';
import SrUtilitySetupLazySignup from 'client/components/SrUtilitySetup/SrUtilitySetupLazySignup';
import SrUtilitySetupProviderPlanConfirmation from 'client/components/SrUtilitySetup/SrUtilitySetupProviderPlanConfirmation';
import SrUtilitySetupTransferInternetFlowReturnEquipmentModal from 'client/components/SrUtilitySetup/SrUtilityTransferInternetFlow/SrUtilitySetupTransferInternetFlowReturnEquipmentModal';

@withTransitionHook
@withLocationQuery
@withScreenId(SCREEN_ID_UTILITY_SETUP_PROVIDER_PLAN_CONFIRMATION)
@withScrollToTop
@branch(({ match }) => ({
  utilityUser: ['utilityUser'],
  utilityActivation: ['utilityActivations', 'byShortId', match.params.utilityActivationShortId],
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
}))
@branch(({ utilityActivation }) => {
  if (utilityActivation) {
    return {
      activationInternetProviders: [
        'utilityProviders',
        'internetPlansByPartnerAddressId',
        utilityActivation.utilityPartnerAddressId,
      ],
    };
  }
  return {};
})
class UtilitySetupProviderPlanConfirmationPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Utility Setup | Plan Confirmation',
  });

  static fetchData(match, inject) {
    return Promise.all([
      UtilityPartnerActions.getBySlug(inject, {
        slug: match.params.partnerSlug,
      }),
      UtilityActivationActions.getByShortId(inject, {
        utilityActivationShortId: match.params.utilityActivationShortId,
      }),
    ]).then(([, utilityActivation]) => {
      if (utilityActivation) {
        return UtilityActivationActions.getInternetPlans(inject, {
          utilityPartnerAddressId: utilityActivation.utilityPartnerAddressId,
        });
      }
      return Promise.resolve();
    });
  }

  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      showConfirmationModal: false,
      callbackValues: null,
      callbackFunctionName: null,
      returnEquipmentModalArgs: null,
      showAmenityConfirmationModal: false,
    };
  }

  _onCreatePurchaseRequest = (formValues) => {
    const {
      dispatch,
      match,
      utilityActivation,
      utilityUser,
      locationQuery,
      activationInternetProviders,
    } = this.props;

    const { utilityProviderId, utilityProviderPlanId, utilityTypeLabel } = match.params;

    if (hasRoleAnonymous(utilityUser.roles)) {
      this.setState({
        callbackValues: formValues,
        callbackFunctionName: '_onSubmit',
      });

      return;
    }

    this.setState({
      isLoading: true,
    });

    const utilityActivationItem = utilityActivation.items.find(
      (item) => item.type.label.toLowerCase() === utilityTypeLabel,
    );
    const utilityProvider = activationInternetProviders.find(
      (provider) => provider.id === parseInt(utilityProviderId, 10),
    );

    const isTransferToPurchase = locationQuery.newCustomerDeals === 'true';
    const isContractToNoContract = locationQuery.isContractToNoContract === 'true';
    const isInternetAmenityPlan = activationInternetProviders
      .flatMap(({ plans = [] }) => plans)
      .some((plan) => {
        return plan?.internetAmenityPlanId && plan?.id === parseInt(utilityProviderPlanId, 10);
      });
    const isHybridPurchaseFlowType = utilityProvider.purchaseFlowType === UTILITY_PROVIDER_PURCHASE_FLOW_TYPE_HYBRID;

    const payload = {
      utilityActivationItemId: utilityActivationItem.id,
      utilityProviderAnswers: {},
      isTransferToPurchase,
      isContractToNoContract,
      ...setNullEmptyValues(formValues),
    };

    for (const { fieldId } of utilityProvider.questions) {
      payload.utilityProviderAnswers[fieldId] = payload[fieldId];
      delete payload[fieldId];
    }

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_UPDATE_ITEM_PLAN,
      payload: {
        form: global.btoa(JSON.stringify(payload)),
      },
      onSuccess: () => {
        dispatch(UtilityActivationActions.getByShortId, {
          utilityActivationShortId: utilityActivation.shortId,
          forceRefresh: true,
        }).then(() => {
          if (isInternetAmenityPlan && !isHybridPurchaseFlowType) {
            this.setState({
              showAmenityConfirmationModal: true,
            });
          } else {
            this.setState({
              showConfirmationModal: true,
            });
          }
        });
      },
    });
  };

  _onSubmit = (formValues) => {
    const { locationQuery, utilityActivation, match, dispatch } = this.props;
    const isTransferToPurchase = locationQuery.newCustomerDeals === 'true';
    const utilityTypeLabel = match.params.utilityTypeLabel;
    const utilityProviderId = parseInt(match.params.utilityProviderId, 10);
    const utilityActivationItem = utilityActivation.items.find(
      (item) => item.type.label.toLowerCase() === utilityTypeLabel,
    );
    const utilityProvider = utilityActivationItem.availableProviders.find(
      (provider) => provider.id === utilityProviderId,
    );

    if (isTransferToPurchase) {
      return dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_ACTIVATION_TRACK_INTERNET_FLOW,
        payload: {
          utilityActivationId: utilityActivation.id,
          internetFlowEventName: ANALYTICS_EVENT_UTILITY_SETUP_INTERNET_FLOW_RETURN_EQUIPMENT,
        },
      })
        .then(() => {
          return this.setState({
            returnEquipmentModalArgs: {
              providerName: utilityProvider.title,
              onSubmit: () => this._onCreatePurchaseRequest(formValues),
              onClose: () => this.setState({ returnEquipmentModalArgs: null }),
            },
          });
        })
        .catch(() => {
          return this.setState({
            returnEquipmentModalArgs: {
              providerName: utilityProvider.title,
              onSubmit: () => this._onCreatePurchaseRequest(formValues),
              onClose: () => this.setState({ returnEquipmentModalArgs: null }),
            },
          });
        });
    }
    this._onCreatePurchaseRequest(formValues);
  };

  render() {
    const {
      match,
      history,
      locationQuery,
      utilityPartner,
      utilityActivation,
      utilityUser,
      activationInternetProviders = [],
    } = this.props;

    const {
      partnerSlug,
      utilityProviderId,
      utilityProviderPlanId,
      utilityProviderPlanVariantId,
      utilityActivationShortId,
      utilityTypeLabel,
    } = match.params;

    const {
      isLoading,
      showConfirmationModal,
      callbackFunctionName,
      callbackValues,
      returnEquipmentModalArgs,
      showAmenityConfirmationModal,
    } = this.state;

    if (!utilityPartner || !utilityActivation) {
      return null;
    }

    if (!canAccessActivation({
      utilityActivation,
      utilityUser,
    })) {
      return <Redirect to={`/utility-setup/${utilityActivation.partner.slug}/login/${utilityActivation.address.id}`} />;
    }

    const utilityUserEmail = utilityActivation?.user?.email;

    const utilityActivationItem = utilityActivation.items.find(
      (item) => item.type.label.toLowerCase() === utilityTypeLabel,
    );

    const accountRequested =
      utilityActivationItem.status === UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED;

    const isContractToNoContract = locationQuery.isContractToNoContract === 'true';

    const isTransferToPurchase =
      locationQuery.newCustomerDeals === 'true' &&
      utilityActivationItem.type?.id === UTILITY_TYPE_ID_INTERNET;
    const utilityProvider = (activationInternetProviders || []).find(
      ({ id }) => id === parseInt(utilityProviderId, 10),
    );

    const internetAmenityPlanId = activationInternetProviders
      .flatMap(({ plans = [] }) => plans)
      .find((plan) => {
        return plan?.internetAmenityPlanId && plan?.id === parseInt(utilityProviderPlanId || 0, 10);
      })?.internetAmenityPlanId;

    const makeInternetConfirmationModalText = ({ utilityActivation, utilityActivationItem }) => {
      return `Your home internet amenity is now set to ${utilityActivationItem.provider.title
        } with ${numberWithCommas(
          utilityActivationItem.plan.internetSpeedMbps,
        )} Mbps. You'll be emailed at ${utilityActivation.user.email
        } with steps of how to activate your internet amenity upon equipment receival.`;
    };

    return (
      <SrUtilitySetupPage
        addressDisplay={utilityActivation.address?.display}
        utilityProviderTheme={utilityProvider?.theme}
        utilityPartner={utilityPartner}
        utilityActivationId={utilityActivation.id}
        logoUrl={utilityProvider?.retailerLogoUrl}
        backButtonText="Back"
        backButtonUrl={`/utility-setup/${partnerSlug}/${utilityActivationShortId}${accountRequested
          ? ''
          : `/${utilityTypeLabel}/plan${isContractToNoContract ? '-no-contract' : ''
          }?id=${utilityProviderPlanId}&providerPlanVariantId=` +
          `${utilityProviderPlanVariantId || ''}providerId=${utilityProviderId}`
          }`}
        rightActionComponent={
          <SrBox>
            <SrText
              align="right"
              font="helvetica"
              display="block"
              fontSize="10px"
              lineHeight="12px"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              overflow="hidden"
            >
              Trusted Referrer: {utilityPartner.name}
            </SrText>
            <SrText
              align="right"
              font="helvetica"
              display="block"
              fontSize="10px"
              lineHeight="12px"
              textOverflow="ellipsis"
            >
              Processing ID: {utilityActivationShortId}
            </SrText>
          </SrBox>
        }
      >
        <SrUtilitySetupProviderPlanConfirmation
          history={history}
          leaseStartDate={utilityActivation.leaseStartDate}
          isTransferToPurchase={isTransferToPurchase}
          utilityActivationItem={utilityActivationItem}
          utilityProviderId={parseInt(utilityProviderId, 10)}
          utilityActivation={utilityActivation}
          utilityProviderPlanId={parseInt(utilityProviderPlanId, 10)}
          utilityProviderPlanVariantId={parseInt(utilityProviderPlanVariantId, 10) || null}
          utilityProviders={activationInternetProviders || utilityActivationItem.availableProviders}
          internetAmenityPlanId={internetAmenityPlanId}
          isLoading={isLoading}
          onSubmit={this._onSubmit}
        />
        {showConfirmationModal && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Success!"
            underline
            message={`Order submitted! You will be emailed at ${utilityUserEmail} for any updates.`}
            maxWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            buttonText="Ok thanks"
            buttonTheme={BUTTON_THEME_GREEN_ADOBE}
            onButtonClick={() => {
              history.push(`/utility-setup/${partnerSlug}/${utilityActivationShortId}`);
            }}
          />
        )}
        {showAmenityConfirmationModal && (
          <SrSimpleMessageModal
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            iconUrl="/images/checkmark-circle-large-green.svg"
            maxWidth="375"
            headline={
              <SrBox>
                <SrText font="greycliff" weight="800" fontSize="25px" lineHeight="25px">
                  Internet Updated!
                </SrText>
              </SrBox>
            }
            underline
            message={makeInternetConfirmationModalText({
              utilityActivation,
              utilityActivationItem,
            })}
            buttonText="Ok thanks"
            buttonTheme={BUTTON_THEME_GREEN_ADOBE}
            onButtonClick={() => {
              history.push(`/utility-setup/${partnerSlug}/${utilityActivationShortId}`);
            }}
          />
        )}
        {callbackFunctionName && (
          <SrUtilitySetupLazySignup
            utilityActivation={utilityActivation}
            callbackFunction={() => {
              this[callbackFunctionName](callbackValues);
            }}
            onClose={() => this.setState({ callbackFunctionName: null, callbackValues: null })}
          />
        )}
        {returnEquipmentModalArgs && (
          <SrUtilitySetupTransferInternetFlowReturnEquipmentModal {...returnEquipmentModalArgs} />
        )}
      </SrUtilitySetupPage>
    );
  }
}

UtilitySetupProviderPlanConfirmationPage.displayName = 'UtilitySetupProviderPlanConfirmationPage';

export default UtilitySetupProviderPlanConfirmationPage;
